import { useState } from 'react'
import * as ToastPrimitive from '@radix-ui/react-toast'
import Button from '@components/atoms/Button'
import cn from '@components/utils/cn'
import Icon from '@components/utils/Icon'
import { scopedTranslation } from '@utils/I18n'
import { useToastStore, type Toast, type ToastType } from '@stores/toastStore'

const t = scopedTranslation('shared')

const typeStyles = {
  success: { icon: 'check', badge: 'tw:bg-success-600', bar: 'tw:bg-success-500', live: 'background' },
  error: { icon: 'alert-circle', badge: 'tw:bg-error-600', bar: 'tw:bg-error-500', live: 'foreground' },
  info: { icon: 'info-circle', badge: 'tw:bg-primary-600', bar: 'tw:bg-primary-500', live: 'background' },
  warning: { icon: 'alert-triangle', badge: 'tw:bg-warning-600', bar: 'tw:bg-warning-500', live: 'foreground' },
} as const

function ProgressBar({ type, duration, paused }: { type: ToastType; duration: number; paused: boolean }) {
  if (duration === Infinity) return null

  return (
    <div className="tw:absolute tw:bottom-0 tw:left-0 tw:right-0 tw:h-0.5 tw:overflow-hidden tw:rounded-b-lg">
      <div
        className={cn('toast-progress-bar tw:h-full tw:rounded-full', typeStyles[type].bar)}
        style={{
          animation: `toast-shrink ${duration}ms linear forwards`,
          animationPlayState: paused ? 'paused' : 'running',
        }}
      />
    </div>
  )
}

function ToastItem({ toast, onRemove }: { toast: Toast; onRemove: (id: string) => void }) {
  const [paused, setPaused] = useState(false)

  return (
    <ToastPrimitive.Root
      className={cn(
        'toast-item tw:relative tw:flex tw:items-start tw:gap-3 tw:rounded-lg tw:border tw:border-gray-200 tw:bg-white tw:p-4 tw:shadow-xs tw:overflow-hidden',
        'tw:data-[state=open]:animate-in tw:data-[state=open]:fade-in-0 tw:data-[state=open]:slide-in-from-top-2',
        'tw:data-[state=closed]:animate-out tw:data-[state=closed]:fade-out-0 tw:data-[state=closed]:slide-out-to-right-full',
        'tw:data-[swipe=move]:translate-x-(--radix-toast-swipe-move-x)',
        'tw:data-[swipe=cancel]:translate-x-0 tw:data-[swipe=cancel]:transition-transform',
        'tw:data-[swipe=end]:animate-out tw:data-[swipe=end]:slide-out-to-right-full'
      )}
      type={typeStyles[toast.type].live}
      duration={toast.duration}
      onPause={() => setPaused(true)}
      onResume={() => setPaused(false)}
      onOpenChange={(open) => {
        if (!open) onRemove(toast.id)
      }}
    >
      <div
        className={cn(
          'tw:flex tw:h-8 tw:w-8 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full',
          typeStyles[toast.type].badge
        )}
      >
        <Icon type={typeStyles[toast.type].icon} width={16} height={16} className="tw:text-white" />
      </div>

      <div className="tw:flex tw:flex-col tw:justify-center tw:gap-0.5 tw:flex-1 tw:min-w-0 tw:min-h-8">
        <ToastPrimitive.Title className="tw:text-sm tw:font-medium tw:text-gray-900">
          {toast.title}
        </ToastPrimitive.Title>
        {toast.description && (
          <ToastPrimitive.Description className="tw:text-sm tw:text-gray-500">
            {toast.description}
          </ToastPrimitive.Description>
        )}
        {toast.action && (
          <ToastPrimitive.Action altText={toast.action.label} asChild>
            <Button
              rank="link"
              size="sm"
              className="tw:w-fit tw:px-0"
              onClick={() => {
                toast.action?.onClick()
                onRemove(toast.id)
              }}
            >
              {toast.action.label}
            </Button>
          </ToastPrimitive.Action>
        )}
      </div>

      <ToastPrimitive.Close
        className="tw:-m-1 tw:flex tw:h-8 tw:w-8 tw:shrink-0 tw:items-center tw:justify-center tw:self-start tw:rounded-full tw:text-gray-400 tw:hover:bg-gray-100 tw:hover:text-gray-600 tw:cursor-pointer"
        aria-label={t('buttons.close')}
      >
        <Icon type="x-close" width={14} height={14} />
      </ToastPrimitive.Close>

      <ProgressBar type={toast.type} duration={toast.duration} paused={paused} />
    </ToastPrimitive.Root>
  )
}

export default function Toaster() {
  const toasts = useToastStore((s) => s.toasts)
  const removeToast = useToastStore((s) => s.removeToast)

  return (
    <ToastPrimitive.Provider swipeDirection="right">
      {toasts.map((item) => (
        <ToastItem key={item.id} toast={item} onRemove={removeToast} />
      ))}
      <div className="tailwind">
        <ToastPrimitive.Viewport className="tw:fixed tw:top-4 tw:right-4 tw:z-100 tw:flex tw:flex-col tw:gap-2 tw:w-96 tw:max-w-[calc(100vw-2rem)] tw:list-none" />
      </div>
    </ToastPrimitive.Provider>
  )
}
